/**
 * Los bloques del cuestionario (§7), con la redacción literal del documento.
 *
 * §7 define 13 (0-12). Aquí hay 12: el Bloque 7 (escenarios de alianza) se
 * eliminó por decisión del comando y la numeración NO se corrió, para que el
 * resto siga coincidiendo con la del documento.
 *
 * ORDEN DE LOS BLOQUES (§7): primero lo espontáneo, luego lo asistido; primero
 * personajes, luego partidos; las preguntas que mencionan partidos junto a
 * personajes al final. Así ninguna pregunta "enseña" un nombre o una etiqueta
 * partidista antes de medirse sin ayuda. El motor de la PWA respeta este orden
 * de forma estricta y no permite navegar hacia adelante saltando bloques.
 */
import { BIRTHPLACE, COLONIA_SERVICES, CONSIDERATION, DIRECTION, EDUCATION, GOVERNOR_PREFERENCE, GOV_RATING, HOUSEHOLD_INCOME, KNOWS_PERSONAJE, OCCUPATION, OPINION_PARTY, OPINION_PERSONAJE, PARTY_ASSOCIATION, PARTY_IDENTIFICATION, PARTY_LEAN, PARTY_REJECTION, PARTY_SYMPATHY, POLITICAL_INTEREST, SERVICE_RATING, SEX, TRUST, VINCULATION_THEMES, VOTE_DISPOSITION, VOTE_RECALL_2024_MUNICIPAL, VOTE_RECALL_2024_PRESIDENTIAL, YEARS_IN_TECATE, YES_NO, YES_NO_NC, } from './scales.js';
import { MEDIOS_OPTIONS, ORGANIZACION_OPTIONS } from '../catalog/codebooks.js';
import { PERSONAJES } from '../catalog/personajes.js';
/* ========================================================================== */
/*                    Tamaño de la batería de personajes                      */
/* ========================================================================== */
/**
 * Cuántos personajes lleva la tarjeta.
 *
 * Se lee del catálogo en lugar de escribirse en la redacción, porque la cifra
 * aparece en cuatro textos que el encuestador lee o ve en pantalla y, cuando
 * estaba escrita a mano, añadir un personaje dejaba el instrumento diciendo
 * «los 14 nombres» sobre una tarjeta de 15.
 */
const N_PERSONAJES = PERSONAJES.length;
/**
 * Minutos del Bloque 3, escalados al tamaño real de la batería.
 *
 * §7 publica «6–7» para este bloque con los 14 personajes de la v3.1. Es un
 * barrido por pregunta: el costo crece de forma casi lineal con cada nombre
 * añadido, así que la cifra del documento se divide entre 14 y se multiplica
 * por los personajes que realmente hay, redondeando al medio minuto —la
 * granularidad con la que está hecha toda la tabla de §7.
 *
 * Sin esto, añadir el 15º personaje habría dejado el presupuesto de tiempo
 * declarando 6–7 minutos para un bloque que tarda más, que es precisamente el
 * dato que §8 compara contra el piloto.
 */
const N_PERSONAJES_V3_1 = 14;
const BLOCK_3_MINUTES_V3_1 = { min: 6, max: 7 };
function scaleToPersonajes(minutesFor14) {
    const scaled = (minutesFor14 / N_PERSONAJES_V3_1) * N_PERSONAJES;
    return Math.round(scaled * 2) / 2;
}
/* ========================================================================== */
/*                 Bloque 0 · Contacto, selección y filtros                   */
/* ========================================================================== */
const BLOCK_0 = {
    id: 0,
    title: 'Contacto, selección y filtros',
    estimatedMinutes: 1.5,
    readAloudIntro: 'Buenos días/tardes. Mi nombre es [nombre], de Calimetría, una empresa de investigación de opinión. ' +
        'Estamos realizando un estudio sobre Tecate y su comunidad. No vendemos nada. Sus respuestas son anónimas ' +
        'y confidenciales, y se usan sólo con fines estadísticos. La entrevista dura unos 20 minutos. ¿Me podría apoyar?',
    orderingRationale: 'No mencionar partidos ni personajes. No portar prendas, colores ni artículos de ningún partido o personaje.',
    questions: [
        {
            code: 'F1',
            kind: 'yes-no',
            text: '¿Vive usted habitualmente en esta vivienda, aquí en Tecate?',
            options: YES_NO,
            required: true,
            interviewerNote: 'Si responde "No": pedir hablar con un residente habitual de la vivienda. No entrevistar a quien no viva aquí.',
        },
        {
            code: 'F2',
            kind: 'numeric',
            text: '¿Cuántas personas de 18 años o más que tengan credencial para votar viven en esta vivienda?',
            min: 0,
            max: 30,
            required: true,
            methodologyNote: 'Dato indispensable para la ponderación: §6.1 fija la probabilidad de la persona = 1 ÷ número de adultos elegibles en la vivienda. Sin este número el peso base no se puede calcular.',
            terminateIf: {
                condition: { op: 'equals', question: 'F2', value: 0 },
                disposition: 'no_elegible',
                message: 'No hay adultos con credencial para votar en la vivienda. Registrar y pasar a la siguiente.',
            },
        },
        {
            code: 'F3',
            kind: 'person-select',
            text: 'De esas personas, ¿quién cumple años más próximamente?',
            // Opcional y con valor por omisión por decisión del comando (W1-3.1.0).
            // El efecto metodológico está documentado en INSTRUMENT_CHANGES y en
            // DECISIONES §15: deja de registrarse a quién señaló el método.
            required: false,
            defaultText: 'La persona que estoy entrevistando',
            interviewerNote: 'MÉTODO DE CUMPLEAÑOS (§5.3.3). Listar a los adultos residentes con credencial y seleccionar a quien cumpla años más próximamente. El sistema alterna "próximo" / "último" cumpleaños entre encuestadores. Si la persona seleccionada no está, AGENDAR REVISITA: hasta 3 intentos en distintos horarios antes de pasar a la siguiente vivienda del salto.',
            methodologyNote: 'NUNCA sustituir a la persona seleccionada por otra de la misma vivienda para cerrar cuotas de género o edad (§5.3.5). El campo viene con un valor por omisión, pero la REGLA de selección no cambió: hay que aplicar el método del cumpleaños igual.',
        },
        {
            code: 'F4',
            kind: 'yes-no',
            text: '¿Tiene usted 18 años o más y credencial para votar vigente con domicilio en Tecate?',
            options: YES_NO,
            required: true,
            interviewerNote: 'Preguntar a la PERSONA SELECCIONADA en F3.',
            terminateIf: {
                condition: { op: 'equals', question: 'F4', value: 'no' },
                disposition: 'no_elegible',
                message: 'La persona no cumple el criterio del universo. Registrar y terminar.',
            },
        },
        {
            code: 'F5',
            kind: 'gps',
            text: 'Colonia y sección',
            required: true,
            // Lo rellena la PWA con lo que detectó el GPS. Pedirlo a mano era pedir
            // que el encuestador transcribiera un dato que el sistema ya tenía.
            prefillFrom: 'gps-location',
            interviewerNote: 'Lo rellena el sistema con la sección y la colonia que detectó el GPS. Verificar que coincida con la asignación y corregir el texto si hiciera falta; la coordenada se registra aparte y es la que manda.',
            methodologyNote: '§9.4 — GPS OBLIGATORIO: validar que cada captura cae dentro del polígono de la sección INE.',
        },
        {
            code: 'F6',
            kind: 'consent',
            text: 'Lectura del aviso de privacidad simplificado y consentimiento',
            options: [
                { code: 'acepta', label: 'Acepta' },
                { code: 'no_acepta', label: 'No acepta' },
            ],
            required: true,
            methodologyNote: '§11 — Cumplimiento de la Ley Federal de Protección de Datos Personales en Posesión de los Particulares. El aviso simplificado se lee en cada entrevista.',
            terminateIf: {
                condition: { op: 'equals', question: 'F6', value: 'no_acepta' },
                disposition: 'rechazo',
                message: 'Sin consentimiento no se puede continuar. Agradecer y registrar como rechazo.',
            },
        },
    ],
};
/* ========================================================================== */
/*            Bloque 1 · Clima de opinión y problemas públicos                */
/* ========================================================================== */
const BLOCK_1 = {
    id: 1,
    title: 'Clima de opinión y problemas públicos',
    estimatedMinutes: 2,
    questions: [
        {
            code: 'C1',
            kind: 'single',
            text: 'En general, ¿diría usted que las cosas en Tecate van por buen camino o por mal camino?',
            options: DIRECTION,
            required: true,
            isVerificationKey: true,
            objectives: [7],
        },
        {
            code: 'C2',
            kind: 'open',
            text: '¿Cuál considera usted que es el principal problema que debe atender el próximo gobierno de Tecate?',
            interviewerNote: 'ESPONTÁNEA, UNA SOLA RESPUESTA. No leer opciones. Capturar textual y, si es evidente, marcar el código.',
            codebookId: 'problemas',
            // §9.5 enumera C2 entre las abiertas que se codifican en gabinete. Sin
            // esta marca la respuesta se capturaba textual y NUNCA aparecía en la
            // cola de codificación, así que el ranking de problemas de §10 se
            // quedaba sin insumo pese a estar el texto en la base.
            codeInOffice: true,
            required: true,
            isVerificationKey: true,
            objectives: [7],
            methodologyNote: '§8 — ajustar los códigos de C2/C3 tras el piloto. §9.5 — codificación en gabinete contra el libro "problemas". §Bloque 9 — ajustar los temas V1–V6 para que coincidan con los 4–5 problemas más mencionados aquí.',
        },
        {
            code: 'C3',
            kind: 'open',
            text: '¿Y el segundo problema más importante?',
            interviewerNote: 'ESPONTÁNEA. Mismos códigos que C2.',
            codebookId: 'problemas',
            codeInOffice: true,
            objectives: [7],
            methodologyNote: '§9.5 — codificación en gabinete con los mismos códigos de C2.',
        },
        {
            code: 'C4',
            kind: 'matrix',
            text: '¿Cómo califica los siguientes servicios en su colonia?',
            options: SERVICE_RATING,
            rowSource: { kind: 'literal', rows: COLONIA_SERVICES },
            rotateRows: true,
            required: true,
            objectives: [7, 8],
            methodologyNote: 'C4 permite mapear problemas por estrato y sección (objetivo 8) y aterrizar la narrativa a nivel colonia.',
        },
    ],
};
/* ========================================================================== */
/*                  Bloque 2 · Notoriedad espontánea (NÚCLEO)                 */
/* ========================================================================== */
const BLOCK_2 = {
    id: 2,
    title: 'Notoriedad espontánea',
    estimatedMinutes: 1.5,
    isCore: true,
    readAloudIntro: 'Ahora pensemos en la política de Tecate. Sin que yo le dé nombres, dígame por favor qué políticos ' +
        'o personajes públicos de Tecate conoce o ha escuchado mencionar.',
    orderingRationale: 'Va ANTES del Bloque 3 (lista asistida) y ANTES del Bloque 4 (evaluación de Román Cota) para no contaminar el top-of-mind. Corrección #5 de la versión 2.',
    questions: [
        {
            code: 'N1',
            kind: 'ranked-open',
            text: '¿Qué políticos o personajes públicos de Tecate conoce o ha escuchado mencionar recientemente?',
            interviewerNote: 'ESPONTÁNEA, HASTA 5 MENCIONES EN ORDEN. NO SUGERIR NINGÚN NOMBRE. La primera mención es el top-of-mind. Capturar textual tal como la persona lo diga.',
            maxSelections: 5,
            codebookId: 'menciones_espontaneas',
            codeInOffice: true,
            required: true,
            objectives: [1],
            methodologyNote: '§10.1.1 — top-of-mind = % que lo menciona en primer lugar; notoriedad espontánea total = % que lo menciona en cualquier lugar. §9.6 — redacción CONGELADA entre oleadas.',
        },
        {
            code: 'N2',
            kind: 'open',
            text: 'Pensando en cualquier persona, ¿quién cree usted que tiene mayor capacidad para gobernar Tecate?',
            interviewerNote: 'ESPONTÁNEA, UNA SOLA. Admite "nadie" y "no sabe". NO limitar a los nombres que mencionó en N1.',
            codebookId: 'menciones_espontaneas',
            codeInOffice: true,
            allowRefusal: true,
            objectives: [3],
            methodologyNote: 'Cambio respecto a v1: N2 ya no se limita a "los que mencionó", para no castigar a quien dio pocas menciones.',
        },
        {
            code: 'N3',
            kind: 'open',
            text: '¿Y a quién no quisiera ver como presidente(a) municipal?',
            interviewerNote: 'ESPONTÁNEA, UNA SOLA. Admite "nadie" y "no sabe".',
            codebookId: 'menciones_espontaneas',
            codeInOffice: true,
            allowRefusal: true,
            objectives: [2],
            methodologyNote: '§10.1.13 — penalización por rechazo espontáneo = % que lo menciona aquí.',
        },
    ],
};
/* ========================================================================== */
/*                  Bloque 3 · Batería de personajes (CORE)                   */
/* ========================================================================== */
const BLOCK_3 = {
    id: 3,
    title: `Batería de ${N_PERSONAJES} personajes`,
    // §7 publica "6–7" para este bloque con 14 personajes. Es el único con
    // rango, y es la razón por la que la tabla de bloques ya sumaba 20–21
    // minutos mientras §2 declara una duración de 18–20: el extremo alto del
    // diseño excedía la meta desde antes de añadir el 15º personaje. §8 existe
    // precisamente para medir la duración real en el piloto y, si se pasa de 20
    // minutos, aplicar los recortes de §8.1.
    estimatedMinutes: scaleToPersonajes(BLOCK_3_MINUTES_V3_1.min),
    estimatedMinutesMax: scaleToPersonajes(BLOCK_3_MINUTES_V3_1.max),
    isCore: true,
    readAloudIntro: 'Ahora le voy a mostrar una lista de personas. Para cada una, dígame si la conoce o ha oído hablar de ella.',
    orderingRationale: `Tarjeta con los ${N_PERSONAJES} nombres, SÓLO nombre de uso público: sin partido, sin foto y sin cargo. El orden se rota aleatoriamente por entrevista y el CAPI CONSERVA EL MISMO ORDEN en todas las preguntas del bloque para ese entrevistado, y también en el Bloque 9. Aplicación POR PREGUNTA, no por personaje: primero I1 para los ${N_PERSONAJES}, después I2 para todos los conocidos, y así sucesivamente. Es más rápido y reduce el efecto halo.`,
    questions: [
        {
            code: 'I1',
            kind: 'matrix',
            text: '¿Conoce o ha oído hablar de [personaje]?',
            options: KNOWS_PERSONAJE,
            rowSource: { kind: 'personajes' },
            rotateRows: true,
            required: true,
            objectives: [1],
            methodologyNote: 'Base: TODOS. §10.1.2 — notoriedad bruta = "conoce bien" + "ha oído". §9.6 — reactivo CONGELADO entre oleadas.',
        },
        {
            code: 'I2',
            kind: 'matrix',
            text: '¿Qué opinión tiene de [personaje]?',
            options: OPINION_PERSONAJE,
            rowSource: { kind: 'personajes-known' },
            rotateRows: true,
            showRowIf: { op: 'knowsRowPersonaje' },
            required: true,
            objectives: [2],
            methodologyNote: 'Base: CONOCEN. §10.1.4 — favorabilidad neta = % buena/muy buena − % mala/muy mala sobre base conocedores. §2.2 — reportar también sobre base total, indicando siempre el tamaño de base.',
        },
        {
            code: 'I3',
            kind: 'matrix',
            text: '¿Qué tanto confiaría en [personaje] para resolver los problemas de Tecate?',
            options: TRUST,
            rowSource: { kind: 'personajes-known' },
            rotateRows: true,
            showRowIf: { op: 'knowsRowPersonaje' },
            required: true,
            objectives: [3],
            methodologyNote: 'Base: CONOCEN. §10.1.5 — confianza efectiva = % "mucho + algo".',
        },
        {
            code: 'I4',
            kind: 'matrix',
            text: '¿Con qué partido político relaciona a [personaje]?',
            options: PARTY_ASSOCIATION,
            rowSource: { kind: 'personajes-known' },
            rotateRows: true,
            showRowIf: { op: 'knowsRowPersonaje' },
            required: true,
            objectives: [4],
            methodologyNote: 'Base: CONOCEN. Responde al objetivo 4: mide si el electorado ubica a cada personaje con su partido real, con otro, o con ninguno. Es clave para saber qué personajes "cargan" la marca de su partido y cuáles pueden transitar a una alianza. §10.1.6.',
        },
        {
            code: 'I5',
            kind: 'matrix',
            text: 'Si [personaje] fuera candidato(a) a la presidencia municipal, ¿usted…?',
            options: VOTE_DISPOSITION,
            rowSource: { kind: 'personajes-known' },
            rotateRows: true,
            showRowIf: { op: 'knowsRowPersonaje' },
            required: true,
            objectives: [2, 6],
            methodologyNote: 'Base: CONOCEN; los indicadores se calculan sobre BASE TOTAL. NO es intención de voto: no enfrenta a personajes entre sí. Mide el PISO ("seguro"), el POTENCIAL ("seguro + podría") y el RECHAZO DURO ("nunca") de cada personaje por separado. Es el mejor predictor temprano de competitividad. §10.1.7–8.',
        },
        {
            code: 'I6',
            kind: 'matrix-open',
            text: '¿Qué es lo primero que le viene a la mente cuando piensa en [personaje]?',
            rowSource: { kind: 'personajes-assigned', assignmentKey: 'i6-personajes' },
            codeInOffice: true,
            trimKey: 'i6-single-personaje',
            objectives: [2],
            interviewerNote: 'ESPONTÁNEA. Capturar textual, sin interpretar.',
            methodologyNote: 'SUBMUESTRA: el CAPI asigna 2 personajes conocidos AL AZAR por entrevistado, PRIORIZANDO A LOS DE MENOR COBERTURA acumulada. Recorte §8.1.3: bajar a 1 personaje por entrevistado si el piloto excede 20 minutos.',
        },
    ],
};
/* ========================================================================== */
/*                   Bloque 4 · Evaluación de gobiernos                       */
/* ========================================================================== */
const BLOCK_4 = {
    id: 4,
    title: 'Evaluación de gobiernos',
    estimatedMinutes: 0.5,
    orderingRationale: 'Se movió DESPUÉS de los bloques 2 y 3 para no mencionar a Román Cota antes de medir la notoriedad espontánea (corrección #5 de la v2).',
    questions: [
        {
            code: 'G1',
            kind: 'single',
            text: '¿Cómo califica el trabajo del presidente municipal, Román Cota Muñoz?',
            options: GOV_RATING,
            required: true,
            isVerificationKey: true,
        },
        {
            code: 'G2',
            kind: 'single',
            text: '¿Y el trabajo de la gobernadora, Marina del Pilar Ávila?',
            options: GOV_RATING,
            required: true,
        },
        {
            code: 'G3',
            kind: 'single',
            text: '¿Y el trabajo de la presidenta de la República, Claudia Sheinbaum?',
            options: GOV_RATING,
            required: true,
        },
    ],
};
/* ========================================================================== */
/*               Bloque 5 · Partidos e identidad partidista                   */
/* ========================================================================== */
const BLOCK_5 = {
    id: 5,
    title: 'Partidos e identidad partidista',
    estimatedMinutes: 2,
    readAloudIntro: 'Hablemos ahora de los partidos políticos.',
    orderingRationale: 'Va después de los personajes para que ninguna etiqueta partidista se "enseñe" antes de medir la notoriedad y la imagen de los personajes sin ayuda (§7).',
    questions: [
        {
            code: 'P1',
            kind: 'single',
            text: 'Independientemente de por quién haya votado, ¿con cuál partido político simpatiza más?',
            options: PARTY_SYMPATHY,
            rotateOptions: true,
            required: true,
            isVerificationKey: true,
            objectives: [5],
            methodologyNote: 'Lista de 9 partidos ROTADA. §10.2.1 — simpatía declarada. §9.6 — reactivo CONGELADO entre oleadas.',
        },
        {
            code: 'P2',
            kind: 'single',
            text: '¿Se considera muy o algo identificado(a) con ese partido?',
            options: PARTY_IDENTIFICATION,
            showIf: {
                op: 'notIn',
                question: 'P1',
                values: ['ninguno', 'ns_nc'],
            },
            required: true,
            objectives: [5],
            methodologyNote: '§10.2.2 — identidad dura = % "muy"; identidad blanda = algo/poco.',
        },
        {
            code: 'P3',
            kind: 'single',
            text: 'Aunque no simpatice con ninguno, ¿hacia cuál se inclina un poco más?',
            options: PARTY_LEAN,
            rotateOptions: true,
            showIf: { op: 'in', question: 'P1', values: ['ninguno', 'ns_nc'] },
            required: true,
            objectives: [5],
            methodologyNote: '§10.2.1 — simpatía con inclinados = P1 + P3.',
        },
        {
            code: 'P4',
            kind: 'matrix',
            text: '¿Qué opinión tiene de cada uno de los siguientes?',
            options: OPINION_PARTY,
            rowSource: { kind: 'parties-plus-jhh' },
            rotateRows: true,
            required: true,
            objectives: [5],
            methodologyNote: '9 partidos ROTADOS más "la coalición Juntos Haremos Historia: MORENA, PVEM y PT", que se lee AL FINAL y no rota. §10.2.3 — imagen neta. §9.6 — reactivo CONGELADO entre oleadas.',
        },
        {
            code: 'P5',
            /**
             * LISTA CERRADA por decisión del comando.
             *
             * §Bloque 5 la define como espontánea de hasta 2 menciones («Abierta →
             * códigos»), y la instrucción original era NO leer la lista. Al pasarla
             * a selección múltiple con los 9 partidos a la vista deja de medir
             * rechazo espontáneo y pasa a medir rechazo asistido: son dos cosas
             * distintas y los porcentajes no son comparables con §10.2.4 tal como
             * está redactado. Ver la nota en PARTY_REJECTION.
             *
             * Como ya no es abierta, tampoco entra a la cola de codificación de
             * §9.5: las respuestas vienen codificadas desde campo.
             */
            kind: 'multi',
            text: '¿Por cuál partido nunca votaría?',
            options: PARTY_REJECTION,
            maxSelections: 2,
            interviewerNote: 'Mostrar la lista. Admite HASTA 2 respuestas.',
            objectives: [5],
            methodologyNote: '§10.2.4 — rechazo partidista, medido de forma ASISTIDA (lista a la vista), no espontánea como en la v3.1. Máximo 2 selecciones; "ninguno" y NS son excluyentes.',
        },
        {
            code: 'P6',
            kind: 'single',
            text: 'En la elección para presidente municipal de Tecate de 2024, ¿por cuál partido o candidato votó?',
            options: VOTE_RECALL_2024_MUNICIPAL,
            required: true,
            objectives: [8],
            methodologyNote: 'Resultado oficial de referencia para validar (cómputo IEEBC): coalición MORENA 55.7%, PES BC 21.4%, PAN 7.0%, PT 5.6%, PRI 3.3%, MC 1.9%, PRD 0.7%, nulos 4.1%; participación 49.0%. El recuerdo de voto suele SOBRESTIMAR al ganador y la participación, por lo que se usa para validación y análisis, no como medida exacta (§11). §6.4 — verificación de la ponderación.',
        },
        {
            code: 'P7',
            kind: 'single',
            text: 'En la elección para presidente de la República de 2024, ¿por quién votó?',
            options: VOTE_RECALL_2024_PRESIDENTIAL,
            required: true,
            objectives: [8],
            methodologyNote: 'Sirve para validar la muestra contra el resultado oficial y como insumo de ponderación de sensibilidad (§6.4).',
        },
        {
            code: 'P8',
            kind: 'single',
            text: 'En 2027 también se elegirá gobernador(a) de Baja California. Sin pensar en candidatos, ¿qué partido o alianza preferiría que gobernara el estado?',
            options: GOVERNOR_PREFERENCE,
            rotateOptions: true,
            required: true,
            objectives: [5, 6],
            methodologyNote: 'Mide el posible EFECTO ARRASTRE de la elección concurrente de gubernatura sobre la municipal. §10.2.7 cruzaba P8 × A1–A12; con el Bloque 7 eliminado, el arrastre se lee contra I5 y X1.',
        },
    ],
};
/* ========================================================================== */
/*              Bloque 6 · Participación (votante probable)                   */
/* ========================================================================== */
const BLOCK_6 = {
    id: 6,
    title: 'Participación — votante probable',
    estimatedMinutes: 0.5,
    questions: [
        {
            code: 'E1',
            kind: 'scale',
            text: 'En una escala del 0 al 10, donde 0 es "seguro no voy a votar" y 10 es "seguro voy a votar", ¿qué tan probable es que vote en la elección de junio de 2027?',
            min: 0,
            max: 10,
            required: true,
            allowRefusal: true,
            objectives: [8],
            methodologyNote: '§9.3 — insumo principal del modelo de votante probable (umbral E1 ≥ 8).',
        },
        {
            code: 'E2',
            kind: 'single',
            text: '¿Qué tanto le interesa la política?',
            options: POLITICAL_INTEREST,
            required: true,
            objectives: [8],
            methodologyNote: '§9.3 — el modelo exige E2 distinto de "nada".',
        },
        {
            code: 'E3',
            kind: 'yes-no',
            text: '¿Sabe en dónde le toca votar (su casilla)?',
            options: YES_NO,
            required: true,
            objectives: [8],
        },
    ],
};
/* ========================================================================== */
/*          Bloque 8 · Experimento de etiqueta partidista (X1)                */
/* ========================================================================== */
const BLOCK_8 = {
    id: 8,
    title: 'Experimento de etiqueta partidista',
    estimatedMinutes: 0.5,
    isCore: true,
    orderingRationale: 'Diseño de BOLETA DIVIDIDA: el CAPI asigna a cada entrevistado UNA SOLA versión, y un solo personaje. Sustituye las preguntas A10–A12 de la v1, donde preguntar las tres variantes a la misma persona generaba anclaje y revelaba al patrocinador.',
    questions: [
        {
            code: 'X1',
            kind: 'single',
            text: 'Si [personaje] fuera candidato a la presidencia municipal por [etiqueta], ¿qué tan probable sería que usted votara por él?',
            options: CONSIDERATION,
            showIf: { op: 'knowsRowPersonaje' },
            required: true,
            objectives: [6],
            methodologyNote: 'Sólo se aplica a quienes conocen al personaje (I1). Con versiones aleatorias, la diferencia entre grupos es un EFECTO CAUSAL LIMPIO de la etiqueta partidista, comparable además con su línea base sin etiqueta (I5). §10.1.14 — reportar la diferencia entre versiones con su intervalo de confianza.',
        },
    ],
};
/* ========================================================================== */
/*                 Bloque 9 · Vinculación personaje–tema                      */
/* ========================================================================== */
const BLOCK_9 = {
    id: 9,
    title: 'Vinculación personaje–tema',
    estimatedMinutes: 1,
    readAloudIntro: 'De las personas de esta lista, ¿a quién asocia más con cada uno de los siguientes temas?',
    orderingRationale: `Tarjeta con los ${N_PERSONAJES} nombres EN EL MISMO ORDEN ROTADO DEL BLOQUE 3 para ese entrevistado, más "ninguno" y NS. Los temas se presentan en orden rotado.`,
    questions: [
        {
            code: 'V',
            kind: 'matrix-single',
            text: '¿A quién asocia más con [tema]?',
            rowSource: { kind: 'literal', rows: VINCULATION_THEMES },
            rotateRows: true,
            required: true,
            trimKey: 'block9-half-themes',
            objectives: [3],
            interviewerNote: `Mostrar la tarjeta de ${N_PERSONAJES} nombres en el mismo orden del Bloque 3. Admite "ninguno" y NS.`,
            methodologyNote: `Las opciones de respuesta son los ${N_PERSONAJES} personajes en el orden rotado de la entrevista + "ninguno" + NS. §10.1.12 — liderazgo temático. §Bloque 9 — ajustar la lista de temas tras el piloto para que coincida con los 4–5 problemas más mencionados en C2/C3. Recorte §8.1.1: 3 de los 5 temas por entrevistado.`,
        },
    ],
};
/* ========================================================================== */
/*                 Bloque 10 · Medios y contacto político                     */
/* ========================================================================== */
const BLOCK_10 = {
    id: 10,
    title: 'Medios y contacto político',
    estimatedMinutes: 1,
    questions: [
        {
            code: 'M1',
            kind: 'multi',
            text: '¿Por qué medios se informa de lo que pasa en Tecate?',
            options: MEDIOS_OPTIONS.map((o) => ({ code: o.code, label: o.label })),
            maxSelections: 2,
            required: true,
            interviewerNote: 'HASTA 2 RESPUESTAS. Marcar además cuál es el PRINCIPAL.',
            methodologyNote: 'Se captura el conjunto de hasta 2 medios y, por separado, cuál de ellos es el principal.',
        },
        {
            code: 'M2',
            kind: 'open',
            text: '¿Qué páginas o cuentas locales sigue para enterarse de Tecate?',
            interviewerNote: 'ABIERTA, HASTA 3 MENCIONES.',
            maxSelections: 3,
            codeInOffice: true,
            trimKey: 'm2-half-sample',
            methodologyNote: 'Recorte §8.1.2: aplicar sólo en la mitad de la muestra.',
        },
        {
            code: 'M3',
            kind: 'yes-no',
            text: 'En los últimos 12 meses, ¿algún partido o político lo ha visitado en su casa o le ha entregado material o apoyo?',
            options: YES_NO,
            required: true,
        },
        {
            code: 'M3_quien',
            kind: 'open',
            text: '¿Quién o cuál partido?',
            showIf: { op: 'equals', question: 'M3', value: 'si' },
            codeInOffice: true,
        },
        {
            code: 'M4',
            kind: 'yes-no',
            text: '¿Participa en algún partido, comité, sindicato, iglesia u organización vecinal?',
            options: YES_NO,
            required: true,
        },
        {
            code: 'M4_tipo',
            kind: 'multi',
            text: '¿De qué tipo?',
            options: ORGANIZACION_OPTIONS.map((o) => ({
                code: o.code,
                label: o.label,
                requiresSpecify: o.code === 'otro',
            })),
            showIf: { op: 'equals', question: 'M4', value: 'si' },
            maxSelections: 6,
        },
    ],
};
/* ========================================================================== */
/*                 Bloque 11 · Sociodemográfico de cierre                     */
/* ========================================================================== */
const BLOCK_11 = {
    id: 11,
    title: 'Sociodemográfico de cierre',
    estimatedMinutes: 1.5,
    orderingRationale: 'Va al final para no gastar la disposición del entrevistado en datos de clasificación. S10 (ingreso) es la última por ser la de mayor no respuesta.',
    questions: [
        {
            code: 'S1',
            kind: 'single',
            text: 'Sexo',
            options: SEX,
            required: true,
            interviewerNote: 'Registrar por observación; preguntar sólo si hay duda.',
            objectives: [8],
            methodologyNote: 'Meta de monitoreo §3.1: 404 mujeres / 396 hombres. NO es cuota: no se sustituye a nadie para llenarla. Variable de raking (§6.3).',
        },
        {
            code: 'S2',
            kind: 'numeric',
            text: '¿Cuántos años cumplidos tiene?',
            min: 18,
            max: 120,
            required: true,
            isVerificationKey: true,
            objectives: [8],
            methodologyNote: 'Variable de raking (§6.3) y base de los grupos operativos de monitoreo §3.2. Si es menor de 18 la entrevista no es elegible: contradice F4. Es además una de las 5 respuestas clave de la verificación telefónica (§9.4): confirma que se entrevistó a la persona seleccionada por el método de cumpleaños y no a otro habitante de la vivienda, que es el incumplimiento que §5.3.5 prohíbe expresamente.',
        },
        {
            code: 'S3',
            kind: 'single',
            text: '¿Cuál es el último grado de estudios que terminó?',
            options: EDUCATION,
            required: true,
            objectives: [8],
            methodologyNote: '§3.3 — variable de CONTROL Y PONDERACIÓN, NO CUOTA: el INE no captura escolaridad en la credencial. Si la desviación respecto al Censo supera ±5 pp, se incluye en el raking.',
        },
        {
            code: 'S4',
            kind: 'single',
            text: '¿Cuál es su ocupación principal?',
            options: OCCUPATION,
            required: true,
            objectives: [8],
        },
        {
            code: 'S5',
            kind: 'numeric',
            text: '¿Cuántas personas viven en su hogar?',
            min: 1,
            max: 30,
            required: true,
            objectives: [8],
        },
        {
            code: 'S6',
            kind: 'single',
            text: '¿Cuántos años tiene viviendo en Tecate?',
            options: YEARS_IN_TECATE,
            required: true,
            objectives: [8],
        },
        {
            code: 'S7',
            kind: 'single',
            text: '¿Dónde nació?',
            options: BIRTHPLACE,
            required: true,
            objectives: [8],
        },
        {
            code: 'S8',
            kind: 'yes-no',
            text: '¿Alguien en su hogar recibe algún programa social del gobierno (pensión, beca, apoyo)?',
            options: YES_NO_NC,
            required: true,
            objectives: [8],
            methodologyNote: 'Variable con ALTO PODER EXPLICATIVO en el voto de la región: apoyo al partido gobernante.',
        },
        {
            code: 'S10',
            kind: 'single',
            text: 'Ingreso mensual aproximado del hogar',
            options: HOUSEHOLD_INCOME,
            objectives: [8],
            methodologyNote: 'Va al final por ser la de mayor no respuesta.',
        },
    ],
};
/* ========================================================================== */
/*                            Bloque 12 · Cierre                              */
/* ========================================================================== */
const BLOCK_12 = {
    id: 12,
    title: 'Cierre',
    estimatedMinutes: 0.5,
    readAloudIntro: 'Hemos terminado. Por control de calidad, un supervisor podría llamarle para confirmar que realicé ' +
        'la entrevista. ¿Me permite anotar su nombre de pila y un teléfono? Es opcional.',
    questions: [
        {
            code: 'Q1',
            kind: 'contact',
            text: 'Nombre de pila (opcional)',
            isPii: true,
            methodologyNote: '§11 — dato de contacto usado ÚNICAMENTE para control de calidad; se elimina tras la auditoría. Se almacena cifrado y queda fuera de toda exportación de análisis.',
        },
        {
            code: 'Q2',
            kind: 'contact',
            text: 'Teléfono (opcional, sólo control de calidad)',
            isPii: true,
            methodologyNote: '§9.4 — insumo de la supervisión telefónica del 20% a las 24–48 h. §11 — se elimina tras la auditoría.',
        },
        {
            code: 'Q3',
            kind: 'auto',
            text: 'Hora de inicio y fin',
            interviewerNote: 'Capturada automáticamente por el CAPI.',
            methodologyNote: '§9.4 — duración mínima: las entrevistas de menos de 10 minutos se auditan y, en su caso, se anulan.',
        },
        {
            code: 'Q4',
            kind: 'open',
            text: 'Observaciones del encuestador',
            interviewerNote: 'Incluir cualquier incidencia relevante. Registrar además si hubo presencia de terceros durante la entrevista.',
        },
        {
            code: 'Q4_terceros',
            kind: 'yes-no',
            text: '¿Hubo presencia de terceros durante la entrevista?',
            options: YES_NO,
            required: true,
            methodologyNote: 'La presencia de terceros puede sesgar las respuestas políticas; se registra para el análisis de calidad.',
        },
    ],
};
export const BLOCKS = [
    BLOCK_0,
    BLOCK_1,
    BLOCK_2,
    BLOCK_3,
    BLOCK_4,
    BLOCK_5,
    BLOCK_6,
    BLOCK_8,
    BLOCK_9,
    BLOCK_10,
    BLOCK_11,
    BLOCK_12,
];
//# sourceMappingURL=blocks.js.map